/**
 * The driver flow, wired to `POST` / `PATCH` / `DELETE /garages/:id/drivers`
 * (owner only — drivers are structural fleet, not a weekend action, §2).
 *
 * A driver is who safety gear hangs off: suits, helmets and restraints are items
 * with `ownerType: 'driver'` pointed at this row (screens/Driver.tsx). The form
 * itself is tiny — a name and an optional competition number — because that is
 * all `createDriverRequest` carries; the gear is added separately, per item.
 *
 * Validation is thin on purpose, the same as the other flows: the contract
 * states the rules (`name` 1–60, `compNumber` ≤ 20) and the API enforces them,
 * so the button mirrors the one rule that matters — a name — rather than
 * re-implementing a second copy that could drift.
 */

import { useState } from 'react';
import { Trash2, User } from 'lucide-react';

import type { CreateDriverRequest, DriverDto, UpdateDriverRequest } from '@svcdue/contracts';

import { useCreateDriver, useDeleteDriver, useUpdateDriver } from '../api/hooks';
import { ApiError } from '../api/client';
import { errorMessage } from '../components/states';
import { Field, SheetHead } from '../components/form';

export interface DriverForm {
  name: string;
  compNumber: string;
}

/**
 * Form strings → the contract's request.
 *
 * Pure and exported for the tests. The one rule it encodes: a blank competition
 * number is *absent*, not the empty string — `optionalText` would coerce `''` to
 * null anyway, but sending `undefined` keeps the request honest about what the
 * user actually typed. Create and update share this shape; `updateDriverRequest`
 * is just its partial, and sending every field on an edit is harmless.
 */
export function toRequest(form: DriverForm): CreateDriverRequest & UpdateDriverRequest {
  const text = (value: string): string | undefined => (value.trim() ? value.trim() : undefined);
  return {
    name: form.name.trim(),
    compNumber: text(form.compNumber),
  };
}

/**
 * A stored driver → the form. The reverse of `toRequest`, for the edit path.
 * There is no free-text-picker subtlety here as there is for cars — every field
 * is a plain string — so this is a straight unpack, with null becoming ''.
 */
export function driverToForm(driver: DriverDto): DriverForm {
  return {
    name: driver.name,
    compNumber: driver.compNumber ?? '',
  };
}

/**
 * One flow, two jobs: add a driver, or edit one — same structure as the car and
 * event sheets. They share the fields and `toRequest`; editing also deletes.
 */
export function AddDriver({ garageId, driver, onClose }: { garageId: string; driver?: DriverDto; onClose: () => void }) {
  const editing = driver !== undefined;
  const [f, setF] = useState<DriverForm>(driver ? driverToForm(driver) : { name: '', compNumber: '' });
  const create = useCreateDriver(garageId);
  // Hooks are unconditional; the unused one for the current mode never fires.
  const update = useUpdateDriver(garageId, driver?.id ?? '');
  const del = useDeleteDriver(garageId);
  const set = <K extends keyof DriverForm>(k: K, v: DriverForm[K]) => setF((s) => ({ ...s, [k]: v }));

  const save = editing ? update : create;
  const [confirmDelete, setConfirmDelete] = useState(false);

  const issue = (path: string) => (save.error instanceof ApiError ? save.error.issueFor(path) : undefined);
  const busy = save.isPending || del.isPending;

  const submit = () => {
    if (!f.name.trim() || busy) return;
    save.mutate(toRequest(f), { onSuccess: onClose });
  };
  const remove = () => {
    if (!driver || busy) return;
    del.mutate(driver.id, { onSuccess: onClose });
  };

  return (
    <div className="sheet">
      <SheetHead
        title={editing ? 'Edit driver' : 'Add driver'}
        onClose={onClose}
        saveLabel={save.isPending ? 'Saving…' : editing ? 'Save' : 'Add driver'}
        onSave={submit}
        saveDisabled={!f.name.trim() || busy}
      />
      <div className="sheet-body">
        <div className="preview-card">
          <div className="car-ic">
            <User size={18} />
          </div>
          <div className="car-id">
            <b>{f.name.trim() || 'New driver'}</b>
            <span>{f.compNumber.trim() ? `Comp #${f.compNumber.trim()}` : 'Safety gear follows the driver'}</span>
          </div>
        </div>

        <div className="form-sec">Identity</div>
        <Field label="Name">
          <input className="input" value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g., Dale Jr." autoFocus />
          {issue('name') && <div className="field-err">{issue('name')}</div>}
        </Field>
        <Field label="Competition number" hint="optional — shown on the licence card">
          <input className="input" value={f.compNumber} onChange={(e) => set('compNumber', e.target.value)} placeholder="e.g., 43" />
          {issue('compNumber') && <div className="field-err">{issue('compNumber')}</div>}
        </Field>

        {save.isError && !issue('name') && !issue('compNumber') && (
          <div className="field-err">{errorMessage(save.error)}</div>
        )}

        <button className="sheet-save-lg" onClick={submit} disabled={!f.name.trim() || busy}>
          {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add driver'}
        </button>

        {/* Delete lives only in the edit sheet, behind a confirming second tap:
            it cascades, taking the driver's safety gear (their suit, helmet and
            restraints) with them, so it is not a stray-click action. */}
        {editing && (
          <div className="danger-zone">
            {del.isError && <div className="field-err">{errorMessage(del.error)}</div>}
            {confirmDelete ? (
              <div className="danger-confirm">
                <span>Delete {driver.name}? This also removes their safety gear.</span>
                <div className="danger-actions">
                  <button className="act" onClick={() => setConfirmDelete(false)} disabled={busy}>
                    Keep
                  </button>
                  <button className="act act-danger" onClick={remove} disabled={busy}>
                    {del.isPending ? 'Deleting…' : 'Delete driver'}
                  </button>
                </div>
              </div>
            ) : (
              <button className="danger-link" onClick={() => setConfirmDelete(true)} disabled={busy}>
                <Trash2 size={13} /> Delete this driver
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
